/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Document Translator Pricing Module
 *
 * Handles pricing calculations for Document Translator product.
 * Pricing formula: $3.00 base fee for 1-100 words + $0.03 per word over 100
 *
 * Examples:
 * - 1 word = $3.00 (base only)
 * - 50 words = $3.00 (base only)
 * - 100 words = $3.00 (base only)
 * - 150 words = $3.00 + (50 × $0.03) = $4.50
 * - 1000 words = $3.00 + (900 × $0.03) = $30.00
 *
 * @module document-translator-pricing
 */

// ============================================================================
// Constants
// ============================================================================

/**
 * Document Translator Stripe Product ID
 */
import { DOCUMENT_TRANSLATOR_PRODUCT_ID } from "@/config/product-ids";
export { DOCUMENT_TRANSLATOR_PRODUCT_ID };

/**
 * Base price for translations (1-100 words)
 * Stored in cents for precision
 */
const BASE_PRICE_CENTS = 300; // $3.00

/**
 * Price per word above the base threshold
 * Stored in cents for precision
 */
const PRICE_PER_WORD_CENTS = 3; // $0.03

/**
 * Base word threshold - no additional charges up to this count
 */
const BASE_WORD_THRESHOLD = 100;

/**
 * Minimum allowed word count for purchase
 * Pricing: $3.00 flat fee for 1-100 words, then $0.03/word over 100
 */
export const MINIMUM_WORD_COUNT = 1;

/**
 * Maximum allowed word count per order
 */
const MAXIMUM_WORD_COUNT = 1000000;

// ============================================================================
// Types
// ============================================================================

/**
 * Result of Document Translator price calculation
 */
interface DocumentTranslatorPriceResult {
  /** Word count validated and used for calculation */
  wordCount: number;
  /** Base price component in cents */
  basePriceCents: number;
  /** Additional word charges in cents (for words > 100) */
  additionalWordsCents: number;
  /** Total price in cents */
  totalPriceCents: number;
  /** Total price in dollars (for display) */
  totalPriceDollars: number;
  /** Number of words above the base threshold */
  wordsAboveBase: number;
  /** Human-readable breakdown for display */
  breakdown: string;
}

/**
 * Validation result for word count
 */
interface WordCountValidationResult {
  /** Whether the word count is valid */
  isValid: boolean;
  /** Error message if invalid */
  error: string | null;
  /** Suggested valid value if invalid */
  suggestedValue: number;
}

// ============================================================================
// Functions
// ============================================================================

/**
 * Check if a product ID is the Document Translator product
 *
 * @param productId - Stripe product ID to check
 * @returns True if this is Document Translator
 */
export function isDocumentTranslator(productId: string): boolean {
  return productId === DOCUMENT_TRANSLATOR_PRODUCT_ID;
}

/**
 * Validate word count for Document Translator purchase
 *
 * @param wordCount - Number of words to validate
 * @returns Validation result with error details
 */
export function validateWordCount(
  wordCount: number,
): WordCountValidationResult {
  // Handle invalid input types
  if (typeof wordCount !== "number" || isNaN(wordCount)) {
    return {
      isValid: false,
      error: "Please enter a valid word count",
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle non-integer
  if (!Number.isInteger(wordCount)) {
    return {
      isValid: false,
      error: "Word count must be a whole number",
      suggestedValue: Math.max(MINIMUM_WORD_COUNT, Math.round(wordCount)),
    };
  }

  // Handle negative
  if (wordCount < 0) {
    return {
      isValid: false,
      error: "Word count cannot be negative",
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle zero
  if (wordCount === 0) {
    return {
      isValid: false,
      error: "Word count cannot be zero",
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle below minimum
  if (wordCount < MINIMUM_WORD_COUNT) {
    return {
      isValid: false,
      error: `Minimum word count is ${MINIMUM_WORD_COUNT}. Your document has ${wordCount.toLocaleString()} words.`,
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle above maximum
  if (wordCount > MAXIMUM_WORD_COUNT) {
    return {
      isValid: false,
      error: `Maximum word count is ${MAXIMUM_WORD_COUNT.toLocaleString()}. Please split your document into smaller parts.`,
      suggestedValue: MAXIMUM_WORD_COUNT,
    };
  }

  // Valid
  return {
    isValid: true,
    error: null,
    suggestedValue: wordCount,
  };
}

/**
 * Calculate Document Translator price based on word count
 *
 * Pricing formula: $3.00 base + $0.03/word over 100
 *
 * @param wordCount - Number of words to translate
 * @returns Price calculation result with breakdown
 *
 * @example
 * ```typescript
 * const result = calculateDocumentTranslatorPrice(150);
 * // result.totalPriceDollars = 4.50
 * // result.breakdown = "$3.00 base + 50 words × $0.03 = $4.50"
 * ```
 */
export function calculateDocumentTranslatorPrice(
  wordCount: number,
): DocumentTranslatorPriceResult {
  // Validate first
  const validation = validateWordCount(wordCount);
  const validWordCount = validation.isValid
    ? wordCount
    : validation.suggestedValue;

  // Calculate additional words (above base threshold)
  const wordsAboveBase = Math.max(0, validWordCount - BASE_WORD_THRESHOLD);

  // Calculate price components
  const basePriceCents = BASE_PRICE_CENTS;
  const additionalWordsCents = wordsAboveBase * PRICE_PER_WORD_CENTS;
  const totalPriceCents = basePriceCents + additionalWordsCents;
  const totalPriceDollars = totalPriceCents / 100;

  // Build human-readable breakdown
  let breakdown: string;
  if (validWordCount <= BASE_WORD_THRESHOLD) {
    breakdown = `$${(basePriceCents / 100).toFixed(2)} (base price for up to ${BASE_WORD_THRESHOLD} words)`;
  } else {
    breakdown =
      `$${(basePriceCents / 100).toFixed(2)} base + ` +
      `${wordsAboveBase.toLocaleString()} words × $${(PRICE_PER_WORD_CENTS / 100).toFixed(2)} = ` +
      `$${totalPriceDollars.toFixed(2)}`;
  }

  return {
    wordCount: validWordCount,
    basePriceCents,
    additionalWordsCents,
    totalPriceCents,
    totalPriceDollars,
    wordsAboveBase,
    breakdown,
  };
}

/**
 * Format word count for display with proper localization
 *
 * @param wordCount - Number of words
 * @returns Formatted string like "1,234 words"
 */
export function formatWordCount(wordCount: number): string {
  const count = Math.max(0, Math.floor(wordCount));
  const unit = count === 1 ? "word" : "words";
  return `${count.toLocaleString()} ${unit}`;
}

/**
 * Get pricing formula description for UI display
 *
 * @returns Human-readable pricing formula
 */
export function getPricingFormula(): string {
  return `$${(BASE_PRICE_CENTS / 100).toFixed(2)} base + $${(PRICE_PER_WORD_CENTS / 100).toFixed(2)}/word over ${BASE_WORD_THRESHOLD}`;
}

